"use client";

import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { cn } from "utils";
import { GroupedVirtualList } from "@ospweb/sdk-ui";
import { ReportItem } from "./ReportItem/ReportItem";
import {
  type ReportsTimelineGroupedData,
  type ReportTimelineItem,
} from "./utils/reportTimeline";
import Translations from "@translate/components/CoPilot/360Reports/ReportsList";
import { I18nUtil } from "@utils/i18n";
import { EmptyState } from "@/src/components/Empty";
import CardLoading from "@/src/components/Chat/Cards/CardLoading";

const I18n = I18nUtil.createScoped(Translations);

interface ReportsListProps {
  className?: string;
  data: ReportsTimelineGroupedData;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore?: () => void;
  error?: Error | null;
}

/**
 * ReportsList 列表容器组件
 * 显示360报告列表，使用 VirtualList 优化性能
 *
 * @param props ReportsListProps
 * @returns React.ReactElement
 */
export const ReportsList: FC<ReportsListProps> = memo(
  ({
    className,
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onLoadMore,
  }) => {
    const [expandedItemKeys, setExpandedItemKeys] = useState<Set<string>>(
      new Set(),
    );

    /**
     * 动态计算列表项高度
     * 根据数据内容计算不同的高度
     */
    const getItemHeight = useCallback(
      (_item: ReportTimelineItem, isExpanded: boolean) => {
        if (isExpanded) {
          // 展开高度 = 基础高度 + 表格高度
          return 500;
        }
        return 80;
      },
      [],
    );
    // 使用 ref 稳定 onLoadMore 的引用，避免 onEndReached 频繁变更
    const onLoadMoreRef = useRef(onLoadMore);
    useEffect(() => {
      onLoadMoreRef.current = onLoadMore;
    }, [onLoadMore]);

    const groupKeys = useMemo(() => Object.keys(data), [data]);

    const handleEndReached = useCallback(() => {
      if (
        hasNextPage &&
        !isLoading &&
        !isFetchingNextPage &&
        onLoadMoreRef.current
      ) {
        onLoadMoreRef.current();
      }
    }, [hasNextPage, isLoading, isFetchingNextPage]);

    const renderGroupHeader = useCallback(
      (groupKey: string) => (
        <div className="flex items-center rounded bg-background_solid_secondary w-full">
          <span className="body_m text-content_tertiary">{groupKey}</span>
        </div>
      ),
      [],
    );

    const renderTimelineItem = useCallback(
      (
        item: ReportTimelineItem,
        _index: number,
        isExpanded: boolean,
        toggleExpand: () => void,
      ) => {
        return (
          <ReportItem
            reportData={{ ...item, short_name: item?.assets?.[0]?.short_name }}
            viewFullInModal={true}
            isExpanded={isExpanded}
            toggleExpand={toggleExpand}
            buttonText={I18n.t("New Report")}
          />
        );
      },
      [],
    );

    const renderLoading = useMemo(() => {
      if (!hasNextPage || !isFetchingNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center pt-4 pb-20">
          <CardLoading className="w-full max-w-[420px] min-h-0" />
        </div>
      );
    }, [hasNextPage, isFetchingNextPage]);

    const renderNoMore = useMemo(() => {
      if (hasNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center pt-4 pb-20">
          <span className="body_s text-content_quaternary">
            {I18n.t("No more reports")}
          </span>
        </div>
      );
    }, [hasNextPage]);

    if (isLoading && !groupKeys.length) {
      return (
        <div className="flex flex-col w-full h-full items-center justify-center">
          <CardLoading className="w-full max-w-[480px] min-h-[180px]" />
        </div>
      );
    }

    if (!groupKeys.length) {
      return (
        <div className="flex flex-col w-full h-full items-center justify-center">
          <EmptyState
            title={I18n.t("No Smart Signals Yet")}
            description={I18n.t(
              "Check other agents, I’ll alert you when this one finds something worth your attention.",
            )}
            className="w-full max-w-[320px]"
          />
        </div>
      );
    }

    return (
      <div
        className={cn("flex flex-col w-full pb-4 flex-1 min-h-0", className)}
        data-testid="reports-list-container"
      >
        <GroupedVirtualList
          data={data}
          getItemKey={(item) => item.id ?? ""}
          renderItem={(item, index, isExpanded, toggleExpand) =>
            renderTimelineItem(item, index, isExpanded, toggleExpand)
          }
          renderGroupHeader={renderGroupHeader}
          enableAutoHeightDetection
          estimatedItemHeight={80}
          getItemHeight={(item) => getItemHeight(item, false)}
          itemGap={16}
          groupGap={8}
          groupToItemGap={8}
          groupHeaderHeight={20}
          hasNextPage={hasNextPage}
          onEndReached={handleEndReached}
          onEndReachedThreshold={80}
          renderLoading={renderLoading}
          renderNoMore={renderNoMore}
          expandedItemKeys={expandedItemKeys}
          itemClass="max-md:not-last:border-b max-md:not-last:pb-4 border-border_white_transparent10 max-md:[&:has(+_[data-group-key])]:border-none max-md:[&:has(+_[data-nomore])]:border-none max-md:not-last:[&:has(+_[data-nomore])]:pb-0 max-md:not-last:[&:has(+_[data-group-key])]:pb-2"
          onItemToggle={(key, isExpanded) => {
            setExpandedItemKeys((prev) => {
              const next = new Set(prev);
              if (isExpanded) {
                next.add(key);
              } else {
                next.delete(key);
              }
              return next;
            });
          }}
          className="flex-1 min-h-0"
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.isLoading !== nextProps.isLoading ||
      prevProps.isFetchingNextPage !== nextProps.isFetchingNextPage ||
      prevProps.hasNextPage !== nextProps.hasNextPage ||
      prevProps.error !== nextProps.error ||
      prevProps.className !== nextProps.className
    ) {
      return false;
    }

    const prevKeys = Object.keys(prevProps.data);
    const nextKeys = Object.keys(nextProps.data);
    if (prevKeys.length !== nextKeys.length) {
      return false;
    }

    for (const key of prevKeys) {
      const prevItems = prevProps.data[key] || [];
      const nextItems = nextProps.data[key] || [];
      if (prevItems.length !== nextItems.length) {
        return false;
      }
    }

    return true;
  },
);

ReportsList.displayName = "ReportsList";
